import { useEffect, useState } from 'react'
import { apiClient } from '../services/apiClient'
import { useWorkspaceContext } from '../contexts/WorkspaceContext'
import { useThemeStore } from '../store/themeStore'
import type { WhiteLabelSettings } from '../types/whiteLabel'
import { DEFAULT_WHITE_LABEL_SETTINGS } from '../types/whiteLabel'
import { isValidWorkspaceId } from '../utils/workspaceId'

/**
 * Hook to load and apply white label settings from the active workspace
 */
export const useWhiteLabel = () => {
  const { activeWorkspace } = useWorkspaceContext()
  const { setCustomColors } = useThemeStore()
  const [whiteLabelSettings, setWhiteLabelSettings] = useState<WhiteLabelSettings>(DEFAULT_WHITE_LABEL_SETTINGS)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const loadWhiteLabelSettings = async () => {
      // Validate workspace exists and has a proper ID format before making API calls
      if (!activeWorkspace || !activeWorkspace.id || !isValidWorkspaceId(activeWorkspace.id)) {
        // Only warn for truly invalid IDs (not empty/undefined)
        if (activeWorkspace?.id && !isValidWorkspaceId(activeWorkspace.id)) {
          console.warn('⚠️ Invalid workspace ID format, skipping white label load:', activeWorkspace.id)
        }
        // Reset to default if no workspace or invalid ID
        setWhiteLabelSettings(DEFAULT_WHITE_LABEL_SETTINGS)
        setCustomColors({})
        return
      }

      try {
        setLoading(true)
        // Debug logging disabled to reduce console spam
        const response = await apiClient.get(`/api/workspaces/${activeWorkspace.id}/white-label`)

        if (response.data) {
          const settings: WhiteLabelSettings = response.data
          setWhiteLabelSettings(settings)

          // Apply custom colors to theme if white labeling is enabled
          if (settings.enabled) {
            setCustomColors({
              customPrimary: settings.primaryColor,
              customSecondary: settings.secondaryColor,
              customAccent: settings.accentColor,
            })
          } else {
            // Reset to default colors if white labeling is disabled
            setCustomColors({})
          }
        }
      } catch (error) {
        console.error('Failed to load white label settings:', error)
        // Fallback to defaults
        setWhiteLabelSettings(DEFAULT_WHITE_LABEL_SETTINGS)
        setCustomColors({})
      } finally {
        setLoading(false)
      }
    }

    loadWhiteLabelSettings()

  }, [activeWorkspace?.id]) // Only re-run when workspace ID actually changes

  return {
    whiteLabelSettings,
    loading,
    isWhiteLabelEnabled: whiteLabelSettings.enabled,
  }
}
